import { useDispatch, useSelector } from "react-redux";
import { accountActions, getAccountState } from "../redux/slices/accountSlice";
import { AccountData, NotificationType } from "@markit/common.types";
import {
  getStoredAccountIds,
  removeAccountData,
  saveAccountData,
} from "../redux/localStorage";
import { firebaseAuth, signInWithCustomToken } from "../firebase";
import { API } from "../API";
import { onProfilePageNavigatePath } from "../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import { useMemo } from "react";
import { showNotificationBanner } from "../utils/notificationUtils";
import { USER_SWITCHED_ACCOUNT } from "../redux/actionTypes";
import { getUserSnap } from "../utils/FirebaseUtils";
import { filterUndefinedValues } from "@markit/common.utils";

export const useMultiAccountManager = () => {
  const dispatch = useDispatch();
  const { additionalAccountData, accountData, originalAccountAdmin } =
    useSelector(getAccountState).account;
  const navigate = useNavigate();

  const allAccounts = useMemo(() => {
    return [accountData, ...additionalAccountData];
  }, [accountData, additionalAccountData]);

  // Switch to an additional account and clear the current account redux data
  // Don't navigate if called from addAdditionalAccount
  const switchToAdditionalAccount = async (
    newAccountId: string,
    noNavigate?: boolean
  ) => {
    if (originalAccountAdmin) {
      alert(
        "Switch off of the admin mode viewing account before switching accounts."
      );
      return;
    }

    // Remove the new account from the additional account local storage and save the current account to additional
    removeAccountData(newAccountId);
    saveAccountData(accountData.uid);

    // Sign in using custom token generated from backend with uid
    const { customToken } = await API.user.createAuthCustomToken({
      uid: newAccountId,
    });
    await signInWithCustomToken(firebaseAuth, customToken);
    localStorage.setItem("accountMismatch", Date.now().toString());

    // Clear the current redux state and then switch to the new account in redux
    dispatch({ type: USER_SWITCHED_ACCOUNT });
    dispatch(accountActions.clearData());
    dispatch(accountActions.switchToAccount(newAccountId));

    const foundAccount = additionalAccountData.find(
      (account) => account.uid === newAccountId
    );
    if (foundAccount && !noNavigate) {
      if (foundAccount.inCreatorMode) {
        navigate("/home");
      } else {
        onProfilePageNavigatePath(navigate, foundAccount.username);
      }
    }
  };

  // Used when you login to another account and need to clear the current data for the previous account
  const addAdditionalAccount = async (
    prevAccountData: AccountData,
    newAccountData: AccountData,
    noNavigate?: boolean
  ) => {
    // Check if already logged into this account
    const existingAccount = allAccounts.find(
      (account) => account.uid === newAccountData.uid
    );
    if (existingAccount) {
      await switchToAdditionalAccount(newAccountData.uid, noNavigate);
      showNotificationBanner(
        dispatch,
        "Already signed in to this account",
        NotificationType.AFFIRMATIVE
      );
      return;
    }

    localStorage.setItem("accountMismatch", Date.now().toString());
    dispatch({ type: USER_SWITCHED_ACCOUNT });
    dispatch(accountActions.addAccount({ prevAccountData, newAccountData }));
    dispatch(accountActions.clearData());

    // Save previous account data to localStorage to keep a record of it
    saveAccountData(prevAccountData.uid);
  };

  // Load additional accounts from localStorage
  const loadAdditionalAccountsFromStorage = async () => {
    try {
      const storedAccountIds = getStoredAccountIds();
      const currentAccountId = originalAccountAdmin || accountData.uid;

      // Filter out the current account to avoid duplicates
      const additionalAccountIds = storedAccountIds.filter(
        (accountId) => accountId !== currentAccountId
      );

      const additionalAccounts = await Promise.all(
        additionalAccountIds.map(async (accountId) => {
          const userSnap = await getUserSnap(accountId);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            return userData;
          } else {
            // remove outdated data from async storage
            removeAccountData(accountId);
          }
        })
      );
      const definedAdditionalAccounts: AccountData[] =
        filterUndefinedValues(additionalAccounts);

      // Update the additional accounts in the state
      if (additionalAccounts.length > 0) {
        dispatch(
          accountActions.setAdditionalAccounts(definedAdditionalAccounts)
        );
      }
    } catch (error) {
      console.error(
        "Error loading additional accounts from localStorage:",
        error
      );
    }
  };

  return {
    addAdditionalAccount,
    switchToAdditionalAccount,
    loadAdditionalAccountsFromStorage,
  };
};
